import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, ShieldCheck, KeyRound } from "lucide-react";
import axios from "axios";
import { setAdminToken, formatApiError } from "./aapi";
import { EASE } from "@/components/Motion";
import { inputCls } from "@/panel/components";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.post(`${base}/api/auth/login`, {
        identifier: form.email,
        password: form.password,
      });
      if (data.user.role !== "admin") {
        setError("This account does not have admin access.");
        setLoading(false);
        return;
      }
      setAdminToken(data.token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-ink px-4 py-16">
      <div className="pointer-events-none absolute -top-32 right-1/4 h-[480px] w-[480px] rounded-full bg-gold/[0.06] blur-[140px]" />
      <div className="noise-overlay pointer-events-none fixed inset-0 opacity-[0.05]" />

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE }}
        className="relative w-full max-w-md"
        data-testid="admin-auth-card"
      >
        <Link
          to="/"
          data-testid="admin-back-link"
          className="mb-8 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-neutral-500 transition-colors hover:text-goldlight"
        >
          <ArrowLeft size={14} /> BACK TO SITE
        </Link>

        <div className="rounded-3xl border border-gold/20 bg-panel/80 p-8 backdrop-blur-xl sm:p-10">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-gold/40 bg-gold/10 text-gold">
              <ShieldCheck size={20} />
            </span>
            <div>
              <div className="text-sm font-bold tracking-[0.2em] text-neutral-100">
                BEATKING<span className="text-gold">SMM</span>
              </div>
              <div className="font-mono text-[9px] tracking-[0.3em] text-gold/70">ADMIN CONSOLE</div>
            </div>
          </div>

          <h1 className="mt-8 font-serif text-3xl italic text-neutral-50" data-testid="admin-auth-heading">
            The throne room.
          </h1>
          <p className="mt-2 text-sm text-neutral-400">
            Complete control of the platform. Every action is audit-logged.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-auth-form">
            <input
              type="email"
              required
              placeholder="Admin email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={inputCls}
              data-testid="admin-email-input"
            />
            <input
              type="password"
              required
              placeholder="Password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className={inputCls}
              data-testid="admin-password-input"
            />

            {error && (
              <motion.p
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-xl border border-rose-400/30 bg-rose-400/10 px-4 py-3 text-sm text-rose-300"
                data-testid="admin-auth-error"
              >
                {error}
              </motion.p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-button"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3.5 text-sm font-bold tracking-[0.15em] text-black shadow-[0_8px_30px_rgb(var(--gold)_/_0.25)] transition-shadow duration-300 hover:shadow-[0_8px_40px_rgb(var(--gold)_/_0.45)] disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={15} />}
              ENTER ADMIN CONSOLE
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
